import * as z from 'zod';

/**
 * Legacy delivery-client domain schemas — the `/clients/all` lists served by
 * the main gateway (`admin/vendors/with-orders`, `admin/clients`,
 * `admin/vendor-employees`), migrated from cpanel `features/clients`.
 *
 * Wire tolerance rule (same as `entities/vendor-info`): every field is
 * optional/nullish and objects are `.loose()` — the backend evolves faster
 * than this screen, and a missing field must render its "—" fallback, never
 * crash the table. One row schema covers all five legacy segments; the
 * segment decides which fields are read (see `widgets/merchants-table`).
 */

export const clientTypeSchema = z.enum([
  'all-vendor',
  'business-vendor',
  'vendor',
  'employees',
  'client',
]);
export type ClientType = z.infer<typeof clientTypeSchema>;

export const businessTypeSchema = z.enum(['ALL', 'CORPORATE', 'NON-CORPORATE']);
export type BusinessType = z.infer<typeof businessTypeSchema>;

export const vendorStatusFilterSchema = z.enum(['active', 'archive']);
export type VendorStatusFilter = z.infer<typeof vendorStatusFilterSchema>;

/** One grouped-orders bucket (`grouped_orders` on with-orders rows). */
export const legacyGroupedOrderPointSchema = z
  .object({
    date: z.string().nullish(),
    order_count: z.union([z.string(), z.number()]).nullish(),
    total_price: z.union([z.string(), z.number()]).nullish(),
  })
  .loose();
export type LegacyGroupedOrderPoint = z.infer<typeof legacyGroupedOrderPointSchema>;

export const salesManagerRefSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .loose();
export type SalesManagerRef = z.infer<typeof salesManagerRefSchema>;

/**
 * One legacy list row. Vendor rows come from `vendors/with-orders`, recipient
 * rows from `clients`, staff rows from `vendor-employees`; the union of the
 * fields the table reads is declared once so a segment switch never reparses.
 */
export const legacyClientRowSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    display_id: z.union([z.string(), z.number()]).nullish(),
    name: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    business_name: z.string().nullish(),
    official_business_name: z.string().nullish(),
    phone: z.string().nullish(),
    business_number: z.string().nullish(),
    email: z.string().nullish(),
    status: z.string().nullish(),
    logo: z.string().nullish(),
    business_logo: z.string().nullish(),
    profile_photo: z.string().nullish(),
    is_business: z.boolean().nullish(),
    is_corporate: z.boolean().nullish(),
    is_sanctioned: z.boolean().nullish(),
    is_order_blocked: z.boolean().nullish(),
    balance: z.union([z.string(), z.number()]).nullish(),
    minimal_balance: z.union([z.string(), z.number()]).nullish(),
    enable_balance_restrictions: z.boolean().nullish(),
    diff_balance: z.union([z.string(), z.number()]).nullish(),
    total_delivery_price: z.union([z.string(), z.number()]).nullish(),
    total_order_price: z.union([z.string(), z.number()]).nullish(),
    avg_delivery_price: z.union([z.string(), z.number()]).nullish(),
    average_cheque: z.union([z.string(), z.number()]).nullish(),
    order_count: z.union([z.string(), z.number()]).nullish(),
    orders_count: z.union([z.string(), z.number()]).nullish(),
    created_at: z.string().nullish(),
    createdAt: z.string().nullish(),
    create_aAt: z.string().nullish(),
    created_date: z.string().nullish(),
    inn: z.string().nullish(),
    contract_number: z.string().nullish(),
    vendor_name: z.string().nullish(),
    requirements: z.string().nullish(),
    sales_manager: salesManagerRefSchema.nullish(),
    grouped_orders: z.array(legacyGroupedOrderPointSchema).nullish(),
  })
  .loose();
export type LegacyClientRow = z.infer<typeof legacyClientRowSchema>;

/** Paginated envelope shared by all three legacy list endpoints. */
export const legacyClientListSchema = z.object({
  data: z.array(legacyClientRowSchema).default([]),
  count: z.number().default(0),
});
export type LegacyClientList = z.infer<typeof legacyClientListSchema>;

/** Tariff option for the filter select (`GET admin/tariffs`). */
export const legacyTariffOptionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    name: z.string().nullish(),
    price: z.union([z.string(), z.number()]).nullish(),
    range: z.union([z.string(), z.number()]).nullish(),
    price_per_km_after_range: z.union([z.string(), z.number()]).nullish(),
    free_wait_time: z.union([z.string(), z.number()]).nullish(),
    price_per_minute_after_wait: z.union([z.string(), z.number()]).nullish(),
  })
  .loose();
export type LegacyTariffOption = z.infer<typeof legacyTariffOptionSchema>;

/** Back-office admin option for the sales-manager picker (`GET admin/users`). */
export const legacyAdminOptionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .loose();
export type LegacyAdminOption = z.infer<typeof legacyAdminOptionSchema>;
